import React, { useEffect, useState } from "react";
import { FilaBaja, HistBaja, bajasApi, correoApi, errorTexto, fechaHora } from "./correo.service";

/**
 * Correo de Bajas (pestaña de Buzones, Super Admin): a dónde va el correo de cada persona deshabilitada. Su buzón ya no deja entrar ni enviar, pero
 * sigue recibiendo y todo se reenvía (con copia) a su jefa; si la jefa se fue, sube por la cadena hasta Super Admin; o a un reemplazo que se elige aquí.
 */
const Cambiar: React.FC<{ fila: FilaBaja; alGuardar: (reemplazo: number | null, aplicar: boolean) => void; alCerrar: () => void; ocupado: boolean }> = ({ fila, alGuardar, alCerrar, ocupado }) => {
	const [q, setQ] = useState("");
	const [res, setRes] = useState<{ id: number; nombre: string; email: string | null }[]>([]);
	useEffect(() => {
		if (q.trim().length < 2) { setRes([]); return; }
		const t = setTimeout(() => correoApi.personas(q.trim()).then((r) => setRes(r.data.personas.filter((p) => p.id !== fila.user_id))).catch(() => setRes([])), 250);
		return () => clearTimeout(t);
	}, [q, fila.user_id]);
	return (
		<div className="bj-cambiar">
			<b>Reemplazo de {fila.nombre}</b>
			<input className="av-buscar" autoFocus placeholder="Buscar persona…" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Buscar reemplazo" />
			{res.length ? (
				<ul className="bj-res">
					{res.map((p) => (
						<li key={p.id}><button className="av-btn chico" disabled={ocupado} onClick={() => alGuardar(p.id, true)}>{p.nombre}<small> {p.email || ""}</small></button></li>
					))}
				</ul>
			) : null}
			<div className="av-acciones">
				{fila.reemplazo ? <button className="av-btn chico" disabled={ocupado} onClick={() => alGuardar(null, true)}>Quitar Reemplazo (Vuelve a Su Jefa)</button> : null}
				<button className="av-btn chico" onClick={alCerrar}>Cancelar</button>
			</div>
			<span className="av-tenue">Al elegir, el reenvío cambia al momento.</span>
		</div>
	);
};

const BajasCorreo: React.FC = () => {
	const [d, setD] = useState<{ automatico: boolean; bajas: FilaBaja[] } | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [ok, setOk] = useState<string | null>(null);
	const [ocupado, setOcupado] = useState<string | null>(null);
	const [cambiando, setCambiando] = useState<number | null>(null);
	const [hist, setHist] = useState<{ id: number; filas: HistBaja[] } | null>(null);
	const [buscar, setBuscar] = useState("");

	const cargar = () => bajasApi.listado().then((r) => setD(r.data)).catch((e) => setError(errorTexto(e)));
	useEffect(() => { cargar(); }, []);

	const hacer = async (clave: string, fn: () => Promise<any>, msg: string) => {
		setOcupado(clave); setError(null); setOk(null);
		try { await fn(); setOk(msg); setCambiando(null); setHist(null); await cargar(); } catch (e) { setError(errorTexto(e)); } finally { setOcupado(null); }
	};
	const verHist = async (id: number) => {
		if (hist?.id === id) { setHist(null); return; }
		try { setHist({ id, filas: (await bajasApi.historial(id)).data.historial }); } catch (e) { setError(errorTexto(e)); }
	};

	if (!d) return <div className="av-vacio">{error || "Cargando…"}</div>;
	const q = buscar.trim().toLowerCase();
	const filas = d.bajas.filter((b) => !q || (b.nombre + " " + b.email).toLowerCase().includes(q));
	const pendientes = d.bajas.filter((b) => b.estado !== "aplicado").length;

	return (
		<div className="bj">
			<div className="av-acciones bj-cab">
				<input className="av-buscar" placeholder="Buscar persona o correo…" value={buscar} onChange={(e) => setBuscar(e.target.value)} />
				{pendientes ? <button className="av-btn pri" disabled={!!ocupado} onClick={() => hacer("todas", () => bajasApi.aplicarTodas(), "Listo: los reenvíos quedaron como dice el plan.")}>{ocupado === "todas" ? "Aplicando…" : "Aplicar Todas (" + pendientes + ")"}</button> : null}
			</div>
			{!d.automatico ? <div className="av-tenue co-nota">Todavía no se aplica solo: aquí ves a dónde se enviaría el correo de cada baja y lo aplicas con Aplicar. Se enciende con la mudanza.</div> : null}
			{error ? <div className="av-aviso oro" onClick={() => setError(null)}>{error}</div> : null}
			{ok ? <div className="av-aviso ok" onClick={() => setOk(null)}>{ok}</div> : null}
			{!filas.length ? <div className="av-vacio">{d.bajas.length ? "Nada con esa búsqueda." : "Nadie deshabilitado tiene buzón."}</div> : (
				<ul className="co-lista bj-lista">
					{filas.map((b) => {
						const va = b.aplicado || b.plan;
						return (
							<li key={b.user_id} className="co-bloque">
								<div className="bj-fila">
									<div className="co-nom"><b>{b.nombre}</b><small>{b.email}</small></div>
									<div className="bj-destino">
										<span className="av-tenue">{b.estado === "aplicado" ? "Va a" : "Se enviaría a"}</span>
										<b>{va ? (va.nombre || va.email) : "Nadie"}</b>
										<small>{va ? va.motivo_texto : "No hay a quién mandarlo"}{b.estado === "aplicado" && b.aplicado?.desde ? " · desde " + fechaHora(b.aplicado.desde) : ""}</small>
									</div>
									<i className={"co-chip" + (b.estado === "aplicado" ? " activo" : " problema")}>{b.estado === "aplicado" ? "Aplicado" : b.estado === "error" ? "Con Problema" : "Pendiente"}</i>
									<div className="bj-botones">
										{b.estado !== "aplicado" ? <button className="av-btn chico pri" disabled={!!ocupado || !b.plan} onClick={() => hacer("a" + b.user_id, async () => {
											const r = (await bajasApi.aplicar(b.user_id)).data;
											if (!r.ok) throw new Error(r.mensaje);
										}, "Reenvío aplicado.")}>Aplicar</button> : null}
										<button className="av-btn chico" onClick={() => { setCambiando(cambiando === b.user_id ? null : b.user_id); setHist(null); }}>Cambiar</button>
										<button className="av-btn chico" onClick={() => verHist(b.user_id)}>{hist?.id === b.user_id ? "Ocultar Historial" : "Historial"}</button>
									</div>
								</div>
								{b.error ? <div className="bj-error">{b.error}</div> : null}
								{b.reemplazo ? <div className="av-tenue bj-nota">Reemplazo elegido: {b.reemplazo.nombre}</div> : null}
								{cambiando === b.user_id ? (
									<Cambiar fila={b} ocupado={!!ocupado} alCerrar={() => setCambiando(null)}
										alGuardar={(r, ap) => hacer("c" + b.user_id, () => bajasApi.reemplazo(b.user_id, r, ap && d.automatico === true ? true : ap), r ? "Reemplazo guardado." : "Reemplazo quitado.")} />
								) : null}
								{hist?.id === b.user_id ? (
									<div className="bj-hist">
										{!hist.filas.length ? <span className="av-tenue">Sin cambios registrados.</span> : (
											<table>
												<thead><tr><th>Fecha</th><th>De → A</th><th>Motivo</th><th>Quién</th></tr></thead>
												<tbody>{hist.filas.map((h, i) => (
													<tr key={i}><td>{fechaHora(h.fecha)}</td><td>{(h.de || "—") + " → " + (h.a || "—")}</td><td>{h.nota || h.motivo_texto}</td><td>{h.actor}</td></tr>
												))}</tbody>
											</table>
										)}
									</div>
								) : null}
							</li>
						);
					})}
				</ul>
			)}
		</div>
	);
};

export default BajasCorreo;
